/* istanbul ignore file */
import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { ModalHeader, ModalBody } from 'reactstrap';
import './AiDesignPanel.scss';
import axios from 'axios';
import sleep from 'cd-eft/utils/sleep';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import stores from 'cd-eft/models/Stores';
import { EngineeringMapService } from 'cd-eft/framework/mapServices/EngineeringMapServices';
import { Point } from 'cd-eft/utils/esriTypes';
import { AppConstants } from 'cd-eft/appConstants';
import { aiMove, DynamicWaterwayProfileStore } from 'cd-eft/models/Stores/DynamicProfile/Waterway/DynamicWaterwayProfileStore';

const { waterwayDesignStore: waterwayStore, designStore } = stores.AppStore;

let logText = "";

const aiEndpoint: string = 'aws';   //   === 'local';
/* istanbul ignore next */
interface IAiPanelProps {
  elementId: number;
  handleCloseForm: () => void;
  onDesignEnds: () => void;
  onReachesGenerated: () => void;
  profileStore: DynamicWaterwayProfileStore,
  title: string;
}
/* istanbul ignore next */
interface ICreateAutoDefaultReachesPayload {
  alignedElementDesignParamsId: number;
  minStation: number;
  maxStation: number;
  stationInterval: number;
  cutDepth: number;
  liningMaterial: string;
}

/* istanbul ignore next */
interface IAiStation {
  reachId: string;
  station: number;
  channelElev: number
}

/* istanbul ignore next */
const defaultPayload = {
  alignedElementDesignParamsId: 0,
  minStation: 25.0,
  maxStation: 425.0,
  stationInterval: 100.0,
  cutDepth: 1.5,
  liningMaterial: 'Concrete (0.0130, 10)'
};

/* istanbul ignore next */
async function getPayloadUsinLLM(prompt: string) {
  const url = 'https://eft-set.spatialfrontlab.com/set/aiwdt/generateSetPayload';
  const payload = {
    "dto": "AutoDefaultReachesRequest",
    "message": prompt
  };

  console.log("Prompt: ", prompt);

  let retObj: any;

  try {
    const response = await axios.post(url, payload, {
      headers: {
        'Content-Type': 'application/json'
        // Add authorization or other headers here if required
      }
    });

    const res = await response.data

    // if (res && res.response) {
    //   retObj = JSON.parse(res.response);
    // }
    retObj = res;

    console.log('Generate Payload Response:', retObj);
  } catch (error) {
    console.error('Error posting data:', error);
  }
  return retObj;
}

/* istanbul ignore next */
async function createAutoDefaultReaches(payload: ICreateAutoDefaultReachesPayload): Promise<IAiStation[]> {
  const url = 'https://eft-set.spatialfrontlab.com/set/aiwdt/createAutoDefaultReaches';

  let stations: IAiStation[] = [];
  try {
    const response = await axios.post(url, payload, {
      headers: {
        'Content-Type': 'application/json'
        // Add authorization or other headers here if required
      }
    });

    const data = await response.data;
    // console.log('Response:', response.data);

    stations = (data.waterwayVertexParamDTOList as Array<any>).filter((v) => v.reachId !== 'START' && v.reachId !== 'END').map((v) => {
      return { reachId: v.reachId, station: Number(v.endStation.replace("+", "")), channelElev: v.channelElev };
    });

  } catch (error) {
    console.error('Error posting data:', error);
  }
  return stations;
}

/* istanbul ignore next */
async function generateAlternativeQuestions(waterway_id: number) {
  const url = `https://eft-set.spatialfrontlab.com/set/aiwdt/generateAlternativeQuestionsByAlignedElementDesignParmasId/${waterway_id}`;
  let retObj;
  try {
    const response = await axios.get(url, {
      headers: {
        'Content-Type': 'application/json'
        // Add authorization or other headers here if required
      }
    });

    retObj = await response.data;
  } catch (error) {
    console.error('Error posting data:', error);
  }
  return retObj;
}

const luckyNumber = "42";
const embededKey = ` (${luckyNumber})`;

type Message = {
  role: 'user' | 'ai' | 'ai avatar';
  content: string; // Can include HTML for rich formatting
};

/* istanbul ignore next */
export const AiDesignPanel = observer((props: IAiPanelProps) => {
  const {
    elementId,
    handleCloseForm,
    onDesignEnds,
    profileStore,
    title,
  } = props;

  const [firstQuery, setFirstQuery] = useState(true);
  const [execStatus, setExecStatus] = useState<'idle' | 'trigger design' | '3questions'>('idle');
  const [prompt, setPrompt] = useState<string>("");
  // const [response, setResponse] = useState<string>("");

  const [messages, setMessages] = useState<Message[]>([]);
  let messageStream: Message[] = [];

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setPrompt(event.target.value);
  };

  const getLogs = async (waterway_id: number) => {
    try {
      // three follow-up questions: assessment
      if (execStatus === '3questions' && prompt.includes("overall assess")) {

        messageStream = [...messageStream, { role: "ai avatar", content: "AI Assisted Designer" }];
        setMessages(messageStream);

        logText = "<p>Allow me to review the design...</p>"
        // setResponse(logText);
        messageStream = [...messageStream, { role: 'ai', content: logText }];
        setMessages(messageStream);

        const url = `https://eft-set.spatialfrontlab.com/set/aiwdt/waterwayAssessmentAndRecommendation/${waterway_id}`;
        try {
          const response = await axios.get(url, {
            headers: {
              'Content-Type': 'application/json'
              // Add authorization or other headers here if required
            }
          });

          const data = await response.data;
          logText = "<p>" + data.assessmentSummary + "</p>";
          const suggestions = data.suggestions;
          logText += "<ul>";
          for (let index = 0; index < suggestions.length; index++) {
            const element = suggestions[index];
            logText += "<li>" + element + "</li>";
          }
          logText += "</ul>";
          messageStream = [...messageStream, { role: 'ai', content: logText }];
          setMessages(messageStream);
        } catch (error) {
          console.error('Error posting data:', error);
        }
        return;
      }

      // three follow-up questions: ai assistance
      if (execStatus === '3questions' && (prompt.toLowerCase().includes("what") || prompt.toLowerCase().includes("how") || prompt.toLowerCase().includes("when"))) {
        messageStream = [...messageStream, { role: "ai avatar", content: "AI Assisted Designer" }];
        setMessages(messageStream);

        logText = "<p>Let me take a moment to consider your questions in the context of the design...</p>"
        // setResponse(logText);
        messageStream = [...messageStream, { role: 'ai', content: logText }];
        setMessages(messageStream);

        const url = 'https://eft-set.spatialfrontlab.com/set/aiwdt/waterwayDesignAIAssistance';
        const payload = {
          "alignedElementDesignParamsId": waterway_id,
          "userRequest": prompt
        };

        console.log("Prompt: ", prompt);

        try {
          const response = await axios.post(url, payload, {
            headers: {
              'Content-Type': 'application/json'
              // Add authorization or other headers here if required
            }
          });
          const data = await response.data

          logText = "<p>" + data.queryResponse;
          logText = logText.replace(/\n/g, "<br>") + "</p>";
          // setResponse(logText);
          messageStream = [...messageStream, { role: 'ai', content: logText }];
          setMessages(messageStream);
        } catch (error) {
          console.error('Error posting data:', error);
        }
        return;
      }

      // three follow-up questions: modify the existing waterway design
      // if (execStatus === '3questions' && prompt.includes("modify")) {
      //   logText += "<br><br>What modifications would you like to make to the design?";
      //   setResponse(logText);
      //   return;
      // }

      setExecStatus("trigger design")
      // logging starts
      messageStream = [...messageStream, { role: "ai avatar", content: "AI Assisted Designer" }];
      setMessages(messageStream);

      let starting = "<p>Starting design process...</p>";
      // setResponse(starting);
      messageStream = [...messageStream, { role: 'ai', content: starting }];
      setMessages(messageStream);

      // get payload using LLM
      const aiResponse = await getPayloadUsinLLM(prompt + (firstQuery ? "" : ` alignedElementDesignParamsId=${waterway_id}`));

      let aiPayload: ICreateAutoDefaultReachesPayload;

      try {
        aiPayload = JSON.parse(aiResponse.response) as ICreateAutoDefaultReachesPayload;
      } catch (error) {
        if (error instanceof SyntaxError) {
          const wrong = aiResponse.response;
          console.log("JSON error string: " + wrong);
          const fixed = aiResponse.response.replace(/\b(\d+\.\d+)\.(\d+)\b/g, '$1');
          console.log("JSON corrected string: " + fixed);

          aiPayload = JSON.parse(fixed) as ICreateAutoDefaultReachesPayload;
          console.log("JSON error: " + error.message);
        } else {
          throw error;
        }
      }

      if (!aiResponse.status.toLowerCase().includes("success") || aiPayload.alignedElementDesignParamsId === -999) {
        // const replyNonsense = "Kindly focus your questions on topics pertaining to the waterway design."
        // const replyNonsense = "Feel free to ask any questions you have about the waterway design."
        // const replyNonsense = "Please direct your questions to aspects related to the waterway design."
        const replyNonsense = aiResponse.status.replace("ERROR.", "").trim();
        // setResponse(replyNonsense);
        messageStream = [...messageStream, { role: 'ai', content: replyNonsense }];
        setMessages(messageStream);
        return;
      }

      setFirstQuery(false);


      starting = "<p>Digitizing waterway reaches...</p>"
      // setTimeout(() => setResponse(starting), 300)
      messageStream = [...messageStream, { role: 'ai', content: starting }];
      setTimeout(() => setMessages(messageStream), 300);

      if (EngineeringMapService.instance.mapView) {
        waterwayStore.clearWaterwayLayers(EngineeringMapService.instance.mapView);
      }

      const payload: ICreateAutoDefaultReachesPayload = { ...defaultPayload };

      payload.alignedElementDesignParamsId = waterway_id;
      console.log("Waterway_id:", waterway_id);

      if (aiPayload.minStation) payload.minStation = aiPayload.minStation;
      if (aiPayload.maxStation) payload.maxStation = aiPayload.maxStation;
      payload.stationInterval = Math.ceil((aiPayload.maxStation - aiPayload.minStation) / 4)
      if (aiPayload.cutDepth) payload.cutDepth = aiPayload.cutDepth;
      if (aiPayload.liningMaterial) {
        if (aiPayload.liningMaterial.toLowerCase().includes("concrete")) {
          payload.liningMaterial = "Concrete (0.013, 10)";
        } else if (aiPayload.liningMaterial.toLowerCase().includes("d50")) {
          payload.liningMaterial = "D50=6in Rock Riprap (0.045, 9)";
        } else if (aiPayload.liningMaterial.toLowerCase().includes("gravel")) {
          payload.liningMaterial = "Gravel (0.029, 4)";
        } else if (aiPayload.liningMaterial.toLowerCase().includes("turf")) {
          payload.liningMaterial = "Turf Reinforcement Mat (0.031, 0.63)";
        } else {
          payload.liningMaterial = "Concrete (0.013, 10)";
        }
      }
      console.log("Create Reaches Payload: ", payload);

      let currentStations = await createAutoDefaultReaches(payload);
      // props.onReachesGenerated();
      // console.log("auto reach stations: ", currentStations);

      profileStore.turnOffWaterwayReaches();
      await sleep(500);
      // profileStore.addAiReaches(currentStations);
      profileStore.addAiReachesPiecewise(currentStations);

      let token: "running" | "completed" = "running";
      let steps = "";
      let executionMessage = "42";
      // const instance = axios.create({
      //   httpsAgent: new https.Agent({
      //     rejectUnauthorized: false, // Disables SSL verification for testing
      //   }),
      // });

      // axios.get(`http://sfi-demo-nlb-52c75faa35ad3ebf.elb.us-east-1.amazonaws.com:5000/run?waterway_id=${id}`).then((response) => {
      // axios.get(`https://sfi-demo-nlb-52c75faa35ad3ebf.elb.us-east-1.amazonaws.com:5000/run?waterway_id=${id}`, { httpsAgent: agent }).then((response) => {
      // axios.get(`https://sfi-demo-nlb-52c75faa35ad3ebf.elb.us-east-1.amazonaws.com:5000/run?waterway_id=${id}`).then((response) => {

      const baseUrl = aiEndpoint === "aws" ? "https://py-service.spatialfrontlab.com/run?waterway_id=" : "http://127.0.0.1:5000//run?waterway_id=";

      const idUrl = `${baseUrl}${waterway_id}`;

      let materialKey = "Concrete";
      const fullMaterial = payload.liningMaterial.toLowerCase();
      if (fullMaterial.includes("concrete")) {
        materialKey = "Concrete";
      } else if (fullMaterial.includes("d50")) {
        materialKey = "D50";
      } else if (fullMaterial.includes("gravel")) {
        materialKey = "Gravel";
      } else if (fullMaterial.includes("turf")) {
        materialKey = "Turf";
      }

      const aiUrl = `${idUrl}&maxStation=${payload.maxStation}&minStation=${payload.minStation}&cutDepth=${payload.cutDepth}&material=${materialKey}&stationInterval=${payload.stationInterval}`;

      console.log("AI Model url: ", aiUrl);

      axios.get(aiUrl).then((response) => {
        // console.log('Design result:', response.data);

        steps = response.data.steps ? " in " + response.data.steps + " steps." : "";

        if (response.data.error) {
          executionMessage = "AI model exited with error: " + response.data.error + embededKey;
        }
        token = "completed";
      }).catch((error) => {
        token = "completed";
        if (error instanceof Error) {
          if (error.message.toLowerCase().includes("network error")) {
            executionMessage = "The AI model endpoint is either inaccessible or blocked due to a security or certificate-related issue.";
          } else {
            executionMessage = "The AI model service aborted because of error: " + error.message + embededKey;
          }
        } else {
          executionMessage = "The AI model endpoint is inaccessible because of an unknow error.";
        }

      })

      let currentIndex = 0;

      while (token === "running") {
        sleep(1000);
        try {
          const response = await axios.get(`https://eft-set.spatialfrontlab.com/set/ai-logs/${waterway_id}`);
          const data = await response.data;
          if (data) {
            // const resJson = data.logMessageList;

            const actionJson = data.postNodeActionJson;
            const actions = JSON.parse(actionJson);

            if (actions.length === 0) continue;


            for (let index = currentIndex; index < actions.length; index++) {
              if (designStore.shouldAiZoomToAction) {
                profileStore.zoomToMaxExtent();
                await sleep(300);
              }

              const element = actions[index];
              const action = JSON.parse(element);
              // console.log(action);


              const newAiStation: IAiStation = { reachId: action.nodeId, station: action.postAdjustmentDistance, channelElev: action.postAdjustmentElevation };

              const stationX = designStore.commonHorizontalStretchFactor ? (action.postAdjustmentDistance * designStore.commonHorizontalStretchFactor) : action.postAdjustmentDistance;
              const stationY = action.postAdjustmentElevation * (designStore.commonVerticalStretchFactor || AppConstants.profileStretchFactor);

              const point = new Point({
                x: stationX,
                y: stationY,
                spatialReference: profileStore._profileView?.spatialReference
              })



              let move: aiMove = 180;
              let actionLabel = "";
              let start = 7;
              let color = [0, 128, 0, 255];   // up
              let arrowX = 0;
              let arrowY = 0;
              // let color = [0, 145, 70, 255];   // up
              if (action.elevationAdjustment > 0) {
                move = 0;   // up
                actionLabel = "+" + action.elevationAdjustment.toFixed(1);
                arrowY = -17;
              } else if (action.elevationAdjustment < 0) {
                move = 180;   // down
                start = 30;
                actionLabel = action.elevationAdjustment.toFixed(1);
                // color = [177, 26, 44, 255];   // down
                color = [255, 26, 44, 255];   // down
                arrowY = 17;
              } else {
                if (action.distanceAdjustment > 0) {
                  move = 270;   // right
                  actionLabel = "+" + action.distanceAdjustment.toFixed(1);
                  color = [0, 123, 255, 255];   // right
                  arrowX = -17;
                } else if (action.distanceAdjustment < 0) {
                  move = 90;   // left
                  actionLabel = action.distanceAdjustment.toFixed(1);
                  color = [255, 165, 0, 255];  // left
                  arrowX = 17;
                }
              }
              profileStore.flashAiAction(point, action.nodeId, move, color, arrowX, arrowY);
              profileStore.bubbleUpAiActionLabel(point, action.nodeId, actionLabel, start, color)

              const newStations = [...currentStations]
              const newIndex = newStations.findIndex(s => s.reachId === newAiStation.reachId);
              if (newIndex > 0) {
                newStations.splice(newIndex, 1, newAiStation)
              }
              // console.log("newAiStation: ", newAiStation);
              // console.log("currentStatins: ", currentStations);
              // console.log("newStatios: ", newStations);


              // profileStore.addAiReaches(currentStations, true, { point: reachStationSymbol.reachStationGraySymbol, line: reachLineSymbol.designLineGraySymbol });
              // profileStore.addAiReaches(newStations, false);
              // await sleep(1000);
              if (designStore.shouldAiZoomToAction) profileStore.zoomToAction(point);
              setTimeout(() => {
                profileStore.addAiReaches(newStations);
              }, 500);

              currentStations = newStations;

              let displacement = '0';
              if (action.elevationAdjustment === 0) {
                if (Number.isFinite(action.distanceAdjustment)) displacement = `${action.distanceAdjustment.toFixed(2)} feet horizontally`
              } else {
                if (Number.isFinite(action.elevationAdjustment)) displacement = `${action.elevationAdjustment.toFixed(2)} feet vertically`;
              }

              let totalCut = 'not calculated';
              if (Number.isFinite(action.totalCut)) {
                totalCut = `<b>${action.totalCut.toFixed(1)} cy</b>`;
              }

              let totalFill = 'not calculated'
              if (Number.isFinite(action.totalFill)) {
                totalFill = `<b>${action.totalFill.toFixed(1)} cy</b>`
              }

              logText = `
<p>By moving reach station ${action.nodeId} by ${displacement}, the total cut is ${totalCut} and total fill is ${totalFill}.</p>
<p><b>Flow Characteristics:</b></p>
<ul>
  <li>Velocities: ${action.minVelocity.toFixed(1)} to ${action.maxVelocity.toFixed(1)} ft/s across all reach stations.</li>
  <li>Depths: ${action.minDepth.toFixed(2)} to ${action.maxDepth.toFixed(2)} feet for all reaches.</li>
  <li>Widths: ${action.minWidth.toFixed(2)} to ${action.maxWidth.toFixed(2)} feet for all.</li>
</ul>
<p><b>Slope Conditions:</b> ${action.slopeConditions}</p>`;
              messageStream = [...messageStream, { role: 'ai', content: logText }];
              setMessages(messageStream);
            }
            currentIndex = actions.length;

            // logText = starting + (resJson as Array<string>).join("<br><br>");
            // setResponse(logText);
          }
        } catch (error) {
          token = "completed"
          if (error instanceof Error) {
            if (error.message.toLowerCase().includes("network error")) {
              executionMessage = "The AI model endpoint is inaccessible.";
            } else {
              executionMessage = "The log service aborted because of error: " + error.message + embededKey;
            }
          } else {
            executionMessage = "The log endpoint is inaccessible because of an unknow error.";
          }
        }
      }

      // const introRecommendation = "Overall, the waterway design appears to be of good quality. However, would you consider the following adjustments to further improve it?";




      if (executionMessage === luckyNumber) {
        logText = "<br><br>The design process is now complete.<br>Running calculations...<br><br>Refreshing wateray reaches..."
        // setResponse(logText);
        messageStream = [...messageStream, { role: 'ai', content: logText }];
        setMessages(messageStream);

        await sleep(2000);
        await onDesignEnds()

        logText = "<br><br>Done" + steps;
        // setResponse(logText)
        messageStream = [...messageStream, { role: 'ai', content: logText }];
        setMessages(messageStream);

        // messageStream = [...messageStream, { role: "ai avatar", content: "AI Assisted Designer" }];
        // setMessages(messageStream);

        followUpQuestions();

      } else if (executionMessage.includes(embededKey)) {
        logText = "<br><br>Design ended.<br>Running calculations...<br><br>To refresh wateray reaches"
        // setResponse(logText);
        messageStream = [...messageStream, { role: 'ai', content: logText }];
        setMessages(messageStream);

        await sleep(2000);
        await onDesignEnds()

        logText = "<br><br>Done.<br><br>"   //+ executionMessage;
        // setResponse(logText);
        messageStream = [...messageStream, { role: 'ai', content: logText }];
        setMessages(messageStream);

        // messageStream = [...messageStream, { role: "ai avatar", content: "AI Assisted Designer" }];
        // setMessages(messageStream);
        followUpQuestions();
      }
      else {
        // setResponse("<br><br>" + executionMessage)
        messageStream = [...messageStream, { role: 'ai', content: logText }];
        setMessages(messageStream);
      }

    } catch (error) {
      if (error instanceof Error) {
        MessageCenter.addError(`Failed to fetch site with id ${waterway_id}: ${error.message}`)
      } else {
        MessageCenter.addError(`Failed to fetch site with id ${waterway_id}: Unknown error`);
      }
    }

    if (designStore.shouldAiZoomToAction) profileStore.zoomToMaxExtent();

    function followUpQuestions() {
      messageStream = [...messageStream, { role: "ai avatar", content: "AI Assisted Designer" }];
      setMessages(messageStream);

      logText = "<ul><li>Would you like me to give an overall assessment of the waterway design and suggest improvements?</li>";
      logText += "<li>Do you have any further questions about the waterway? e.g., what is the cut depth for all reaches? how to improve it?</li>";
      logText += "<li>Would you like to modify the existing waterway design? e.g., change the lining material to D50_Rock_Riprap or Gravel to enhance durability and stability of the waterway? change the reach range to 50 to 750 feet to optimize the slope and velocity for better stability?</li>";
      logText += "</ul>";
      messageStream = [...messageStream, { role: 'ai', content: logText }];
      setMessages(messageStream);
      setExecStatus("3questions");
    }
  }

  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const divEl = scrollRef.current;
    if (divEl) {
      divEl.scrollTo({
        top: divEl.scrollHeight,
        behavior: "smooth"
      })
    }
  }, [messages]);

  // useEffect(() => {
  //   const el = textareaRef.current;
  //   if (el) {
  //     el.scrollTo({
  //       top: el.scrollHeight,
  //       behavior: "smooth"
  //     })
  //   }

  // }, [response]);

  return (
    <div className="eft-action-modal modal-ai-form">
      <ModalHeader toggle={() => { setFirstQuery(true); handleCloseForm(); }} className='title-bar py-4'>
        <span className='ai-header-title'>{title}</span>
        {/* <div className="d-flex justify-content-between align-items-center w-100">
          <span className="ai-header-title ps-3">{title}</span>
          <button
            type="button"
            className="border-0 rounded-circle d-flex align-items-center justify-content-center"
            style={{ width: '32px', height: '32px' }}
            onClick={handleCloseForm}
          >
            X
          </button>
        </div> */}
      </ModalHeader>
      <ModalBody>
        <div className="mx-4 pt-3">
          <span className="ai-body-text my-2" >Specify your wateway parameters, I can assist you to design the waterway.</span><br />
          {/* <div className="d-flex align-items-center rounded py-2">
            <textarea id="ai-response-box" ref={textareaRef} className='form-control ai-response-display pb-5 mb-1 border-0 bg-transparent' name="response" rows={17} readOnly value={response}></textarea><br /><br />
          </div> */}
          <div
            id="not-draggable"
            className="mt-3 border border-0 p-0"
            style={{ height: '500px', overflowY: 'auto', fontFamily: '"Source Sans Pro Web", sans-serif', fontSize: '17px' }}
            ref={scrollRef}
          >
            {/* <div className={`p-2 rounded shadow-sm ms-auto'}`} style={{ maxWidth: '80%' }}> */}
            <div className={`p-1 pb-5 rounded ms-auto'}`} >
              {/* <div dangerouslySetInnerHTML={{ __html: response }} /> */}
              {messages.map((msg, idx) => (
                <div key={idx} className="mb-2">
                  {msg.role === 'user' ? (
                    <div className="d-flex align-items-center px-3 py-2 justify-content-end"><span className="fw-normal bg-light rounded p-2">{msg.content}</span></div>
                  ) : msg.role === 'ai' ? (
                    <div className="text-dark px-3" style={{ maxWidth: '87%' }} dangerouslySetInnerHTML={{ __html: msg.content }} />
                  ) : (<div className='d-flex'><div className="icon-circle"><svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" fill="#1E88E5" />
                    <path d="M7 12c0-1.657 2-3 5-3s5 1.343 5 3-2 3-5 3-5-1.343-5-3z" fill="#BBDEFB" />
                    <path d="M18 5l0.5 1.5L20 7l-1.5 0.5L18 9l-0.5-1.5L16 7l1.5-0.5L18 5z" fill="#FFC107" />
                  </svg></div><div className="ai-avatar" style={{ maxWidth: '80%' }} dangerouslySetInnerHTML={{ __html: msg.content }} /></div>)}
                </div>
              ))}
            </div>
          </div>
          <div className="mt-2 ms-5" style={{ fontFamily: "'Source Sans Pro Web', sans-serif" }}>
            <div className="d-flex align-items-center border border-1 rounded px-3 py-2">
              <textarea
                id='ai-prompt-box'
                data-testid="ai-response-area"
                className="form-control border-0 shadow-none"
                placeholder="Ask for more information or details ..."
                rows={1}
                style={{ resize: 'none', fontSize: '17px', lineHeight: '1.4', maxHeight: '100px', overflowY: 'auto' }}
                value={prompt}
                onChange={handleChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    // getLogs(elementId);
                  }
                }}
              />
              <button
                className="btn btn-primary rounded-circle d-flex align-items-center justify-content-center"
                type="button"
                aria-label="send"
                style={{ width: '36px', height: '36px', padding: 0, lineHeight: '1' }}
                onClick={() => {
                  const input = document.querySelector<HTMLTextAreaElement>('textarea');
                  if (prompt) {
                    messageStream = [...messages, { role: 'user', content: prompt }];
                    setMessages(messageStream);
                    setPrompt("");
                    getLogs(elementId);
                  }
                }
                }
                disabled={prompt.length < 10}
              >
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" className="icon-md">
                  <path d="M7.99992 14.9993V5.41334L4.70696 8.70631C4.31643 9.09683 3.68342 9.09683 3.29289 8.70631C2.90237 8.31578 2.90237 7.68277 3.29289 7.29225L8.29289 2.29225L8.36906 2.22389C8.76184 1.90354 9.34084 1.92613 9.70696 2.29225L14.707 7.29225L14.7753 7.36842C15.0957 7.76119 15.0731 8.34019 14.707 8.70631C14.3408 9.07242 13.7618 9.09502 13.3691 8.77467L13.2929 8.70631L9.99992 5.41334V14.9993C9.99992 15.5516 9.55221 15.9993 8.99992 15.9993C8.44764 15.9993 7.99993 15.5516 7.99992 14.9993Z" fill="currentColor"></path>
                </svg>
              </button>
            </div>
          </div>
          {/* Footer Note*/}
          <p className="ai-panel-footnote">
            Please review the AI-generated design and make additional edits.
          </p>
        </div>
      </ModalBody>
      {/* <ModalFooter>
        <div className="row justify-content-end mb-3">
          <button className="usa-button usa-button--outline m-0" onClick={handleCloseForm}>
            Close
          </button>
        </div>
      </ModalFooter> */}
    </div>
  );
});
